/**
 * 2026-10-14 · the step panel redesign — what the new controls WRITE, kept apart from how they look.
 *
 * The redesign is presentational: every patch the panel stages must be one today's panel could
 * already stage, field for field (the "save-equivalence" rule of the design document). These are
 * the only places the new layout builds a patch itself; everything else still calls the same
 * `set(...)` the old sections did. stepPanelModel.test.mjs pins each one against today's shape.
 */

export const SECOND_MEANINGS = ["CHECKED_BY", "VERIFIED_BY", "WITNESSED_BY"];

/** The plain words for a step type: "Parameter record", never "PARAMETER_RECORD". */
export function typeWords(stepType, stepTypes = []) {
  const o = (stepTypes || []).find((t) => t.value === stepType);
  if (o?.label) return o.label;
  const s = String(stepType || "").replace(/_/g, " ").toLowerCase();
  return s ? s[0].toUpperCase() + s.slice(1) : "Step";
}

/** The label to use for a second signer of `meaning` — the first configured label carrying it. */
export function labelFor(meaning, labelOptions = []) {
  const o = (labelOptions || []).find((l) => (l.meaning || l.value) === meaning);
  return o ? o.value : null;
}

/**
 * "+ Add checker": the second signature, as "Checked by". With configured labels it is the
 * `sign_label` today's Signature select writes; without them, the `sign_policy` it writes.
 */
export function addCheckerPatch(labelOptions = [], hasLabels = true) {
  if (!hasLabels) return { sign_policy: "CHECKED_BY" };
  const v = labelFor("CHECKED_BY", labelOptions);
  return v ? { sign_label: v } : null;
}

/** ✕ on the second signer: back to no second signature — today's "NONE" choice. */
export function removeSecondPatch(hasLabels = true) {
  return hasLabels ? { sign_label: "" } : { sign_policy: "NONE" };
}

/** Changing what the second signature means (Checked / Verified / Witnessed). */
export function secondMeaningPatch(value, hasLabels = true) {
  return hasLabels ? { sign_label: value === "NONE" ? "" : value } : { sign_policy: value };
}

/** Whether the step is tracked in CPV: it claims a quality attribute, or is flagged CPP. */
export function cpvOn(step, monitoring) {
  return !!(((step?.cpp_edges || []).length || (step?.cpp || []).length) || (monitoring && monitoring.critical));
}

/**
 * "Track in CPV" turned off: exactly the two writes today's panel would make to undo it —
 * untick every quality attribute (`cpp: []`) and switch "Critical parameter (CPP)" off
 * (`monitoring: {...m, critical: false}`). Nothing is written for what was already off.
 */
export function cpvOffPatch(step, monitoring) {
  const patch = {};
  if ((step?.cpp_edges || []).length || (step?.cpp || []).length) patch.cpp = [];
  if (monitoring && monitoring.critical) patch.monitoring = { ...monitoring, critical: false };
  return patch;
}

/**
 * The state of one area chip (design document, "Chip state"):
 *   content   has something — shows its value or count
 *   add       optional and empty — dashed "+ Add"
 *   missing   required and empty on an editable record — red, opens itself
 *   fact      read-only record — plain, no "+ Add"
 */
export function chipState({ hasContent, required = false, readOnly = false }) {
  if (readOnly) return "fact";
  if (hasContent) return "content";
  return required ? "missing" : "add";
}
